import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { LockClosedIcon, AcademicCapIcon, CheckCircleIcon, ExclamationCircleIcon, KeyIcon, XCircleIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { getApiUrl } from '../config';

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

const ResetPassword: React.FC = () => {
  const { register, handleSubmit, watch, formState: { errors } } = useForm<ResetPasswordFormData>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [tokenChecking, setTokenChecking] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  
  const password = watch('password');
  const token = new URLSearchParams(location.search).get('token');
  
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        console.log('No token found in URL');
        setTokenValid(false);
        setTokenChecking(false);
        return;
      }
      
      console.log('Verifying token:', token.substring(0, 10) + '...');
      
      try {
        const apiUrl = getApiUrl();
        console.log('Sending token verification request to:', `${apiUrl}/auth/verify-reset-token`);
        const response = await axios.get(`${apiUrl}/auth/verify-reset-token?token=${token}`);
        console.log('Token verification response:', response.data);
        
        setTokenValid(true);
      } catch (err: any) {
        console.error('Token verification error:', err);
        
        if (!err.response) {
          console.error('Network error during token verification');
        } else {
          console.error('API error during token verification:', err.response.data);
        }
        
        setTokenValid(false);
      } finally {
        setTokenChecking(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (data.password !== data.confirmPassword) {
      setError('ລະຫັດຜ່ານບໍ່ກົງກັນ');
      return;
    }

    if (data.password.length < 6) {
      setError('ລະຫັດຜ່ານຕ້ອງມີຢ່າງໜ້ອຍ 6 ຕົວອັກສອນ');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${getApiUrl()}/auth/reset-password`, {
        token,
        password: data.password,
      });

      setSuccess('ກູ້ຄືນລະຫັດຜ່ານສຳເລັດແລ້ວ. ກຳລັງກັບໄປໜ້າເຂົ້າສູ່ລະບົບ...');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ. ກະລຸນາລອງໃໝ່ອີກຄັ້ງ');
    } finally {
      setIsLoading(false);
    }
  };

  if (tokenChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600"></div>
          <p className="text-sm text-gray-600 font-medium">ກຳລັງກວດສອບລິ້ງ...</p>
        </div>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="m-20 min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 py-6 sm:py-8 lg:py-12 px-3 sm:px-4 lg:px-6 xl:px-8">
        <div className="max-w-sm sm:max-w-md w-full space-y-6 sm:space-y-8">
          <div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-10 border border-indigo-100 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-100 to-orange-100 rounded-full -mr-16 -mt-16 opacity-50"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-red-50 to-orange-50 rounded-full -ml-12 -mb-12 opacity-50"></div>
            
            <div className="relative z-10">
              <div className="flex justify-center mb-6">
                <div className="bg-gradient-to-r from-red-500 to-orange-600 rounded-full p-4 shadow-lg transform hover:scale-105 transition-transform duration-200">
                  <ExclamationCircleIcon className="h-10 w-10 sm:h-12 sm:w-12 text-white" />
                </div>
              </div>
              <h2 className="text-center text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                ລິ້ງບໍ່ຖືກຕ້ອງ ຫຼື ໝົດອາຍຸ
              </h2>
              <p className="text-center text-sm sm:text-base text-gray-600 mb-8 leading-relaxed">
                ລິ້ງກູ້ຄືນລະຫັດຜ່ານບໍ່ຖືກຕ້ອງ ຫຼື ໝົດອາຍຸແລ້ວ. ກະລຸນາຂໍລິ້ງໃໝ່.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/forgot-password"
                  className="flex-1 flex justify-center items-center py-3 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200 text-sm"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  ຂໍລິ້ງໃໝ່
                </Link>
                <Link
                  to="/login"
                  className="flex-1 flex justify-center items-center py-3 px-6 border-2 border-gray-200 hover:border-indigo-300 text-gray-700 hover:text-indigo-600 font-medium rounded-xl transition-all duration-200 text-sm"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  ກັບຄືນ
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 py-6 sm:py-8 lg:py-12 px-3 sm:px-4 lg:px-6 xl:px-8">
      <div className="max-w-sm sm:max-w-md w-full space-y-6 sm:space-y-8">
        <div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-10 border border-indigo-100 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full -mr-16 -mt-16 opacity-50"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-50 to-indigo-50 rounded-full -ml-12 -mb-12 opacity-50"></div>
          
          <div className="relative z-10">
            <div className="flex justify-center mb-6">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full p-4 shadow-lg transform hover:scale-105 transition-transform duration-200">
                <KeyIcon className="h-10 w-10 sm:h-12 sm:w-12 text-white" />
              </div>
            </div>
            <h2 className="text-center text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              ກູ້ຄືນລະຫັດຜ່ານ
            </h2>
            <p className="text-center text-sm sm:text-base text-gray-600 mb-8 leading-relaxed">
              ປ້ອນລະຫັດຜ່ານໃໝ່ຂອງທ່ານ ເພື່ອກູ້ຄືນການເຂົ້າເຖິງບັນຊີ
            </p>

            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-5">
                <div className="relative">
                  <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-3">
                    ລະຫັດຜ່ານໃໝ່ <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <input
                      id="password"
                      type="password"
                      autoComplete="new-password"
                      required
                      {...register('password')}
                      className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-sm shadow-sm"
                      placeholder="ລະຫັດຜ່ານໃໝ່"
                    />
                  </div>
                </div>
                
                <div className="relative">
                  <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-3">
                    ຢືນຢັນລະຫັດຜ່ານໃໝ່ <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <input
                      id="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      required
                      {...register('confirmPassword')}
                      className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 text-sm shadow-sm"
                      placeholder="ຢືນຢັນລະຫັດຜ່ານໃໝ່"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex justify-center items-center py-4 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      ກຳລັງບັນທຶກ...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      ບັນທຶກລະຫັດຜ່ານໃໝ່
                    </>
                  )}
                </button>
              </div>
            </form>

            {error && (
              <div className="mt-8 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-5">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-red-800 mb-1">
                      ຂໍ້ຜິດພາດ
                    </h3>
                    <div className="text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {success && (
              <div className="mt-8 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-green-800 mb-1">
                      ສຳເລັດ
                    </h3>
                    <div className="text-sm text-green-700">
                      <p>{success}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8 text-center pt-6 border-t border-gray-100">
              <Link 
                to="/login" 
                className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500 transition-colors duration-200"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                ກັບຄືນໄປໜ້າເຂົ້າສູ່ລະບົບ
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 